"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import { Search, Swords, Trophy, Loader2, Check, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { HeartbeatPulse } from "@/components/shared/HeartbeatPulse";
import { captureMetaPixelCustomOnce } from "@/lib/analytics/meta-pixel";
import { type MetaBrowserEvent } from "@/lib/analytics/meta-events";

/* ─── Step definitions ─── */

interface StepDef {
  id: string;
  icon: typeof Search;
  agent: string;
  activeText: string;
  doneText: string;
  flavor: string[];
}

/**
 * Flavor pool for generated debate rounds — rotated per round so no two
 * rounds feel identical even when we debate 4 or 5 niches.
 */
const DEBATE_FLAVORS: string[][] = [
  [
    "Destroyer is poking holes in the revenue model",
    "Seeker defends with market evidence",
    "Testing if this idea can survive real scrutiny",
  ],
  [
    "Destroyer challenges the competitive moat",
    "Seeker argues the timing is right",
    "Stress-testing customer willingness to pay",
  ],
  [
    "Destroyer finds the weakest link",
    "Seeker delivers the closing argument",
    "Only the strongest ideas survive this",
  ],
  [
    "Destroyer pushes on customer acquisition math",
    "Seeker cites reach-by-channel evidence",
    "Can this scale without paid ads?",
  ],
  [
    "Destroyer fires the final salvo",
    "Seeker holds the line on unit economics",
    "Last check before the ranking",
  ],
];

function buildSteps(totalNiches: number): StepDef[] {
  const count = Math.max(1, Math.min(totalNiches, 10));
  const discovering: StepDef = {
    id: "discovering",
    icon: Search,
    agent: "Seeker Agent",
    activeText: "Researching the web for opportunities...",
    doneText: `Found ${count} promising idea${count === 1 ? "" : "s"}`,
    flavor: [
      "Scanning market trends and competitor gaps",
      "Evaluating revenue potential and market size",
      "Identifying underserved niches with real demand",
    ],
  };
  const debateSteps: StepDef[] = Array.from({ length: count }, (_, i) => ({
    id: `debating_${i}`,
    icon: Swords,
    agent: `Debate Round ${i + 1}`,
    activeText:
      i === 0 ? "Seeker pitches first idea..." : `Debate ${i + 1} begins...`,
    doneText: `Idea ${i + 1} stress-tested`,
    flavor: DEBATE_FLAVORS[i % DEBATE_FLAVORS.length],
  }));
  const scoring: StepDef = {
    id: "scoring",
    icon: Trophy,
    agent: "Final Scoring",
    activeText: "Ranking and grading all ideas...",
    doneText: "Results ready!",
    flavor: [
      `Comparing all ${count} ideas head-to-head`,
      "Calculating final scores from debate outcomes",
      "Picking the winner — almost done",
    ],
  };
  return [discovering, ...debateSteps, scoring];
}

const TIPS = [
  "73% of successful startups pivoted from their first idea",
  "The average Foundry idea scores B+. Only 12% get A or higher",
  "Two AI agents debate every idea — only the strongest survive",
  "Your results include revenue estimates, competition analysis, and a difficulty score",
  "The best niches have low competition and high revenue potential",
  "After this, you can generate a full business plan in one click",
];

/* ─── Elapsed time display ─── */

function useElapsed(startIso: string | null, active: boolean): { display: string; seconds: number } {
  const [nowMs, setNowMs] = useState<number | null>(null);
  useEffect(() => {
    if (!startIso) {
      setNowMs(null);
      return;
    }
    setNowMs(Date.now());
    if (!active) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active, startIso]);

  if (!startIso || nowMs === null) return { display: "", seconds: 0 };
  const seconds = Math.max(0, Math.floor((nowMs - new Date(startIso).getTime()) / 1000));
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return { display: min > 0 ? `${min}m ${sec}s` : `${sec}s`, seconds };
}

/**
 * P0-04 UI surface (S98): When a step has been active for ≥30s, show a hint
 * that we're retrying / refining — silent spinner past 30s was the UX pain
 * the backend retry-prompt + Sentry counter (S97) addressed observability for.
 * Tier'd levels match Sentry counter `niche.custom_idea_retry_loop_30s` emit
 * threshold (also 30s) so user-facing text aligns with backend state.
 */
function getRetryHint(seconds: number): string | null {
  if (seconds >= 90) return "Complex idea — giving the model another pass, can take up to a minute.";
  if (seconds >= 60) return "Almost there — refining the details.";
  if (seconds >= 30) return "Taking longer than usual — trying again with a fresh approach.";
  return null;
}

/* ─── Component ─── */

interface NichePreview {
  name: string;
  score: number;
  finalScore: number | null;
  shortDescription: string;
  hasDebate: boolean;
}

interface Props {
  sessionId: string;
  initialStep: string | null;
  initialNiches?: NichePreview[];
  /**
   * Expected total niches in this session. Drives dynamic debate-step count.
   * Defaults to 3 for backwards compat (pre-v3 sessions).
   */
  totalNiches?: number;
}

export function LiveGenerationJourney({
  sessionId,
  initialStep,
  initialNiches,
  totalNiches = 3,
}: Props) {
  const [step, setStep] = useState(initialStep ?? "discovering");
  const [stepAt, setStepAt] = useState<string | null>(null);
  const [heartbeat, setHeartbeat] = useState<string | null>(null);
  const [niches, setNiches] = useState<NichePreview[]>(initialNiches ?? []);
  const [tipIdx, setTipIdx] = useState(0);
  const [flavorIdx, setFlavorIdx] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval>>(null);
  const lastStatusRef = useRef<string>("generating");
  const router = useRouter();

  /**
   * Dynamic: if API tells us a different total (e.g. user ended up with 5),
   * grow. We use the max(prop, niches.length, any observed debate total).
   */
  const observedTotal = useMemo(() => {
    const fromProgress = step.match(/^debating_\d+\/(\d+)$/)?.[1];
    const fromProgressNum = fromProgress ? parseInt(fromProgress, 10) : 0;
    return Math.max(totalNiches, niches.length, fromProgressNum);
  }, [totalNiches, niches.length, step]);

  const STEPS = useMemo(() => buildSteps(observedTotal), [observedTotal]);

  // Poll status
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/session/${sessionId}/status`);
        if (!res.ok) return;
        const data = (await res.json()) as {
          status?: string;
          step?: string;
          stepAt?: string;
          heartbeat?: string;
          niches?: NichePreview[];
          metaEvents?: {
            nicheCompleted?: MetaBrowserEvent;
          };
        };
        if (data.step) setStep(data.step);
        if (data.stepAt) setStepAt(data.stepAt);
        if (data.heartbeat) setHeartbeat(data.heartbeat);
        if (data.niches) setNiches(data.niches);
        if (data.status === "complete") {
          const metaComplete = data.metaEvents?.nicheCompleted;
          if (metaComplete) {
            captureMetaPixelCustomOnce(
              metaComplete.eventName,
              `niche:${sessionId}`,
              {
                generation_type: "niche",
                source: "generation_poll",
              },
              { eventId: metaComplete.eventId },
            );
          }
          if (pollRef.current) clearInterval(pollRef.current);
          // S56: hard reload so SSR page picks up the freshly-saved niches
          // (the Parallel Routes `@grid` / `@detail` slots render on the
          // server, not from polled state here).
          window.location.reload();
        } else if (data.status && data.status !== lastStatusRef.current) {
          // Status transitioned (generating↔failed). pg-boss retry can flip
          // failed→generating mid-stream, so keep polling; just refresh SSR.
          lastStatusRef.current = data.status;
          router.refresh();
        }
      // intentional: 3s session-status poll; transient failure recovers next tick
      } catch {}
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId]);

  // Rotate tips every 8s
  useEffect(() => {
    const id = setInterval(() => setTipIdx((i) => (i + 1) % TIPS.length), 8000);
    return () => clearInterval(id);
  }, []);

  // Rotate flavor text every 4s
  useEffect(() => {
    const id = setInterval(() => setFlavorIdx((i) => i + 1), 4000);
    return () => clearInterval(id);
  }, []);

  // Map DB step to journey index. Dynamic totalNiches-aware.
  const currentStepIdx = (() => {
    if (!step) return 0;
    const direct = STEPS.findIndex((s) => s.id === step);
    if (direct >= 0) return direct;
    // "debating" (no suffix) → first debate active, seeker done
    if (step === "debating") return 1;
    // Dynamic format "debating_N/M" — N rounds completed out of M total.
    // Works with legacy "debating_1/3" AND new "debating_1/5" — any M.
    const progressMatch = step.match(/^debating_(\d+)\/(\d+)$/);
    if (progressMatch) {
      const completed = parseInt(progressMatch[1], 10);
      // STEPS = [discovering, debate_0, debate_1, ..., debate_{N-1}, scoring]
      // After `completed` rounds, the next debate index is completed (0-based),
      // so journey index = 1 (discovering done) + completed.
      return Math.min(1 + completed, STEPS.length - 1);
    }
    return 0;
  })();

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {/* Heartbeat pulse */}
      <div className="flex justify-center">
        <HeartbeatPulse heartbeatIso={heartbeat} />
      </div>

      {/* Step cards */}
      {STEPS.map((s, i) => {
        const isDone = i < currentStepIdx;
        const isActive = i === currentStepIdx;
        const isNext = i > currentStepIdx;

        // For debate steps, show niche name if available
        let nicheName: string | null = null;
        if (s.id.startsWith("debating_") && niches.length > 0) {
          const nicheIdx = parseInt(s.id.split("_")[1], 10);
          nicheName = niches[nicheIdx]?.name ?? null;
        }

        return (
          <StepCard
            key={s.id}
            def={s}
            nicheName={nicheName}
            isDone={isDone}
            isActive={isActive}
            isNext={isNext}
            stepAt={isActive ? stepAt : null}
            flavorIdx={flavorIdx}
          />
        );
      })}

      {/* Tip bar */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-center">
        <p className="text-xs text-white/30 transition-opacity duration-500">
          {TIPS[tipIdx]}
        </p>
      </div>
    </div>
  );
}

/* ─── Step Card ─── */

function StepCard({ def, nicheName, isDone, isActive, isNext, stepAt, flavorIdx }: {
  def: StepDef;
  nicheName: string | null;
  isDone: boolean;
  isActive: boolean;
  isNext: boolean;
  stepAt: string | null;
  flavorIdx: number;
}) {
  const Icon = def.icon;
  const { display: elapsed, seconds: elapsedSec } = useElapsed(stepAt, isActive);
  const currentFlavor = def.flavor[flavorIdx % def.flavor.length];
  const retryHint = isActive ? getRetryHint(elapsedSec) : null;

  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-all duration-500",
        isDone && "border-emerald-500/20 bg-emerald-500/[0.04]",
        isActive && "border-amber-500/30 bg-amber-500/[0.06] shadow-lg shadow-amber-500/5",
        isNext && "border-white/[0.05] bg-white/[0.01] opacity-40"
      )}
    >
      <div className="flex items-center gap-3">
        {/* Status icon */}
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all",
            isDone && "bg-emerald-500/15",
            isActive && "bg-amber-500/15",
            isNext && "bg-white/[0.04]"
          )}
        >
          {isDone ? (
            <Check className="size-5 text-emerald-400" />
          ) : isActive ? (
            <Loader2 className="size-5 text-amber-400 animate-spin" />
          ) : (
            <Icon className={cn("size-5", isNext ? "text-white/20" : "text-white/40")} />
          )}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-sm font-semibold",
              isDone && "text-emerald-400",
              isActive && "text-amber-400",
              isNext && "text-white/30"
            )}>
              {nicheName ? `${def.agent}: ${nicheName}` : def.agent}
            </span>
            {isDone && (
              <span className="rounded-full bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">
                Done
              </span>
            )}
          </div>
          <p className={cn(
            "text-xs mt-0.5",
            isDone && "text-emerald-400/50",
            isActive && "text-white/50",
            isNext && "text-white/15"
          )}>
            {isDone ? def.doneText : isActive ? currentFlavor : def.activeText}
          </p>
        </div>

        {/* Timer */}
        {isActive && elapsed && (
          <div className="flex items-center gap-1 shrink-0">
            <Clock className="size-3 text-amber-400/50" />
            <span className="text-xs font-mono text-amber-400/60 tabular-nums">{elapsed}</span>
          </div>
        )}
      </div>
      {/* P0-04 retry hint — surfaces after 30s/60s/90s of silent spinner */}
      {retryHint && (
        <p className="mt-2 text-[11px] text-amber-300/70 italic pl-[52px]">
          {retryHint}
        </p>
      )}
    </div>
  );
}
